import { useMutation } from '@common/hooks';
import { useCurrentEntity } from '@identity/hooks';
import { Button, Col, Divider, Form, Row, Space, Typography } from 'antd';
import React from 'react';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  DisputeReviewQueryResponse,
  PerformDisputeAction,
} from '@pay-bill/graphql';
import {
  DisputeBodyDetails,
  DisputeCommentInput,
  DisputeDiffLine,
  DisputeDiffSection,
  DisputeFilesInput,
  DisputeFilesSection,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  InvoiceItemDiffLine,
  SimpleDiffLine,
} from '@pay-bill/components';
import { useDisputeDeletedFiles, useDisputeRefetchs } from '@pay-bill/hooks';
import {
  buildEstimationMessage,
  catchErrorWith,
  getRedirectTargetEntity,
  rejectSimpleErrors,
} from '@pay-bill/services';
import { HistoryDropDown } from '@common/components';

const { Text } = Typography;

export const DisputeRedirectDrawerBody = ({
  dispute,
  goBack,
}: {
  dispute: DisputeReviewQueryResponse['dispute'];
  goBack: () => void;
}) => {
  const currentEntity = useCurrentEntity();
  const entityType = currentEntity.entityType;

  const [form] = Form.useForm();
  const [removedFiles, onFileDelete, maxFilesSlots] = useDisputeDeletedFiles(
    entityType === 'Organization'
      ? dispute.files.organization
      : dispute.files.supplier
  );
  const showEstimationMessage = dispute.worksInvoice.preCalculated;

  const redirectEntity = getRedirectTargetEntity(dispute.createdBy?.entityType);
  const action =
    redirectEntity === 'Organization'
      ? 'redirect_to_organization'
      : 'redirect_to_supplier';

  const estimationMessage = buildEstimationMessage(
    entityType,
    dispute.updatedBy?.entityType
  );

  const [performAction, { loading: loadingPerform }] =
    useMutation(PerformDisputeAction);

  const renderTitle = () => {
    if (!dispute) {
      return null;
    }

    const type =
      dispute.disputeType === 'missing_line' ? 'Missing Line' : 'Error';

    return `Send ${type} Dispute`;
  };

  const refetchQueries = useDisputeRefetchs();
  const onFinish = () => {
    if (loadingPerform) {
      return;
    }

    performAction({
      variables: {
        disputeId: dispute.id,
        input: {
          action,
          comment: form.getFieldsValue().comment,
          addedFiles: form.getFieldsValue().files || [],
          removedFiles: removedFiles.map((file) => file.id),
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage('success', `Dispute sent to ${redirectEntity}`);
        goBack();
        refetchQueries();
      })
      .catch(catchErrorWith('Failed to send dispute. Please try again.'));
  };

  return (
    <>
      <DrawerTitle>{renderTitle()}</DrawerTitle>
      <div className="dispute-redirect-drawer-body">
        <DrawerBody>
          <Row className="dispute-dispute-organization-and-unit-section">
            <Col span={8}>
              <Text type="secondary">Organization, Unit</Text>
            </Col>
            <Col span={16} id="dispute-organization-and-unit">
              <span>
                {dispute.organization.name}, {dispute.organizationUnit.name}
              </span>
            </Col>
          </Row>
          <DisputeBodyDetails dispute={dispute} showStatus={false} />
          <DisputeDiffSection mode="view">
            {dispute.disputeType === 'error' && dispute.worksInvoiceItem ? (
              <InvoiceItemDiffLine item={dispute.worksInvoiceItem} />
            ) : (
              <SimpleDiffLine.Empty />
            )}
            <DisputeDiffLine
              current
              dispute={dispute}
              showEstimationSymbol={showEstimationMessage}
            />
            {showEstimationMessage && (
              <p className="estimation-message">{estimationMessage}</p>
            )}
          </DisputeDiffSection>
          <Divider />
          <Form
            form={form}
            initialValues={{
              comment: '',
              files: [],
            }}
            onFinish={onFinish}
          >
            <DisputeCommentInput />
            <Row className="dispute-upload-row">
              <Col span={24}>
                <DisputeFilesInput maxFilesSlots={maxFilesSlots} />
              </Col>
            </Row>
            <Divider />
            <Row>
              <DisputeFilesSection
                dispute={dispute}
                canDeleteFiles={entityType}
                onDelete={onFileDelete}
                hiddenFiles={removedFiles}
              />
            </Row>
          </Form>
          <Divider />
          <HistoryDropDown data={dispute.history} hrVisible={false} />
        </DrawerBody>
      </div>
      <DrawerFooter>
        <Space>
          <Col span={9}>
            <Button
              className="dispute-redirect-button"
              type="default"
              size="large"
              onClick={goBack}
            >
              Back
            </Button>
          </Col>
          <Col span={9}>
            <Button
              className="dispute-redirect-button"
              type="primary"
              size="large"
              onClick={form.submit}
              disabled={loadingPerform}
            >
              Send Dispute
            </Button>
          </Col>
        </Space>
      </DrawerFooter>
      <style jsx>
        {`
          .dispute-redirect-drawer-body {
            flex: 1;
            overflow: auto;
          }
          .dispute-redirect-drawer-body :global(.select-option div) {
            white-space: break-spaces;
            word-break: break-word;
          }

          .dispute-redirect-drawer-body :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }

          .dispute-redirect-drawer-body
            :global(.dispute-dispute-organization-and-unit-section) {
            margin-bottom: 16px;
          }
        `}
      </style>
    </>
  );
};
